import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GetAllOrganizations, GetOrganizationById } from "../../../../api";
import Plus from "../../../assets/svg/icons/common/Plus";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Table from "../../../components/Table";
import useCommonStore from "../../../stores/common";
import useOrganizationStore from "../../../stores/organization";
import useUserStore from "../../../stores/user";
import Spinner from "../../../components/Loaders/Spinner";
import AddOrganizationModal from "../components/add-organization-modal";
import Pagination from "../../../components/Pagination/Pagination";
import styles from "./styles.module.scss";

const getOrganizationImageSrc = (slug) => {
  const count = localStorage.getItem("counter") ? localStorage.getItem("counter") : "";
  return `/img/${slug}/logo-solid.png?${count}`;
};

const List = () => {
  const user = useUserStore((state) => state.user);
  const userLoading = useUserStore((state) => state.userLoading);
  const [organizationDataLoading, setOrganizationDataLoading] = useState(false);
  const organizationList = useOrganizationStore(
    (state) => state.organizationList,
  );
  const setOrganizationList = useOrganizationStore(
    (state) => state.setOrganizationList,
  );
  const setOrgFilterSearchQuery = useOrganizationStore(
    (state) => state.setOrgFilterSearchQuery,
  );
  const orgFilterSearchQuery = useOrganizationStore(
    (state) => state.orgFilterSearchQuery,
  );
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const setSelectedOrganizationId = useOrganizationStore(
    (state) => state.setSelectedOrganizationId,
  );
  const setPageCount = useOrganizationStore((state) => state.setPageCount);
  const pageCount = useOrganizationStore((state) => state.pageCount);
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const editOrganization = useOrganizationStore((state) => state.editOrganization);
  const addOrganizationModalOpen = useOrganizationStore((state) => state.addOrganizationModalOpen);
  const setAddOrganizationModalOpen = useOrganizationStore((state) => state.setAddOrganizationModalOpen)
  const totalItems = useOrganizationStore((state) => state.totalItems);
  const itemsPerPage = useOrganizationStore((state) => state.itemsPerPage);
  const setTotalItems = useOrganizationStore((state) => state.setTotalItems);
  const setItemsPerPage = useOrganizationStore((state) => state.setItemsPerPage);

  const [searchText, setSearchText] = useState("");
  const [organizationUser, setOrganizationUser] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortColumn, setSortColumn] = useState("ASC");

  const getOrganizationType = (type) => {
    const types = {
      hands_on: "Agent/ISO",
      referral_partner: "Referral Partner",
      isv: "ISV",
    };
    return types[type] || type;
  };

  const onRowClick = useCallback(
    (row) => {
      setSelectedOrganizationId(row.id);
      setShowDetails(true);
    },
    [setSelectedOrganizationId, setShowDetails],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "ORGANISATION NAME",
        field: "name",
        width: "236px",
        allowSort: true,
        renderCell: (value, row) => (
          <div className={styles.orgNameCell}>
            <div className={styles.orgNameCellImage}>
              <img className={styles.orgLogo} src={getOrganizationImageSrc(row.whitelabel_slug)} alt="" />
            </div>
            <span className={styles.orgName}>{value}</span>
          </div>
        ),
      },
      {
        headerName: "MTD PROCESSING VOLUME",
        field: "monthly_volume",
        allowSort: true,
        renderCell: (value) =>
          value ? `$${Number(value).toLocaleString()}` : "$0",
      },
      {
        headerName: "OPEN APPS",
        field: "submitting_apps",
        allowSort: true,
        renderCell: (value) => value || "0",
      },
      {
        headerName: "% OPEN APPS",
        field: "open_apps_percentage",
        allowSort: true,
        renderCell: (value, row) => {
          const total = (row.submitting_apps || 0) + (row.approved_apps || 0);
          const percentage = total
            ? (((row.submitting_apps || 0) / total) * 100).toFixed(0)
            : 0;
          return `${percentage}%`;
        },
        hideCell: showDetails,
      },
      {
        headerName: "NON-ACTIVATED APPS",
        field: "approved_apps",
        allowSort: true,
        renderCell: (value) => value || "0",
      },
      {
        headerName: "% NON-ACTIVE",
        field: "non_active_percentage",
        allowSort: true,
        renderCell: (value, row) => {
          const total = (row.submitting_apps || 0) + (row.approved_apps || 0);
          const percentage = total
            ? (((row.approved_apps || 0) / total) * 100).toFixed(0)
            : 0;
          return `${percentage}%`;
        },
        hideCell: showDetails,
      },
      {
        headerName: "APPROVALS (MON)",
        field: "approval_last_month",
        allowSort: true,
        renderCell: (value) => value || "0",
        hideCell: showDetails,
      },
      {
        headerName: "TYPE",
        field: "type",
        width: "151px",
        allowSort: true,
        renderCell: (value) => getOrganizationType(value),
        hideCell: showDetails,
      },
    ],
    [showDetails],
  );

  const fetchData = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      setOrganizationDataLoading(true);
      const allOrganizations = await GetAllOrganizations(
        orgFilterSearchQuery.page,
        {
          searchTerm:
            orgFilterSearchQuery?.search.length > 0
              ? orgFilterSearchQuery.search
              : "",
        },
      );

      if (!allOrganizations?.success) {
        console.error(allOrganizations);
      }

      setTotalItems(allOrganizations.organization.total);
      setItemsPerPage(allOrganizations.organization.per_page);      

      setOrganizationList(allOrganizations.organization.data);
      setPageCount(allOrganizations?.organization?.last_page);
      const organization = await GetOrganizationById(user?.organization?.id);

      setOrganizationUser(organization.users);
      setOrganizationDataLoading(false);
    } catch (error) {
      console.error("error :>> ", error);
      setOrganizationDataLoading(false);
    }
  }, [
    orgFilterSearchQuery.page,
    orgFilterSearchQuery.search,
    setOrganizationList,
    setPageCount,
    user,
  ]);

  useEffect(() => {
    if (!user) {
      return;
    }

    fetchData();
  }, [user, fetchData]);

  // ToDo: Debounce the search input
  useEffect(() => {
    fetchData();
  }, [orgFilterSearchQuery.page, orgFilterSearchQuery.search, fetchData]);

  const handleSort = (field) => {
    setSortBy(field);
    setSortColumn(sortColumn === "ASC" ? "DESC" : "ASC");
  };

  const onChangeSearchInput = (e) => {
    setSearchText(e.target.value);
    setOrgFilterSearchQuery({
      search: e.target.value,
      page: 1,
    });
  };

  if (userLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className={styles.page_heading}>
        <div className={styles.title_contents}>
          <p>Organizations</p>
        </div>
        <div className={styles.right_side}>
          <SearchInput
            onChange={onChangeSearchInput}
            searchValue={searchText}
          />
          <ButtonIcon
            bgColor="#4C70EF"
            textColor="#fff"
            onClick={() => setAddOrganizationModalOpen(true)}
          >
            <Plus />
            <span>Add Organization</span>
          </ButtonIcon>
        </div>
      </div>

      {organizationDataLoading ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : organizationList?.length === 0 ? (
        <div className={styles.noData}>
          <p>No data found</p>
        </div>
      ) : (
        <>
          <Table
            layout="fixed"
            columns={columns}
            data={organizationList}
            sortBy={sortBy}
            sortColumn={sortColumn}
            onSortClick={handleSort}
            onRowClick={onRowClick}
            selectedRowId={selectedOrganizationId}
          />

          {organizationList?.length > 0 && (
            <div className={styles.pagination_container}>
              <Pagination
                pageCount={pageCount}
                currentPage={orgFilterSearchQuery.page - 1}
                onPageChange={({ selected }) => {
                  setOrgFilterSearchQuery({
                    page: selected + 1,
                  });
                }}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}
        </>
      )}

      {addOrganizationModalOpen && (
        <AddOrganizationModal
          open={addOrganizationModalOpen}
          onClose={() => setAddOrganizationModalOpen(false)}
          isEdit={editOrganization}
        // isPcAdmin={true}
        // emsOffices={emsOffices}
        // organization_user={organizationUser}
        // selectedOrganization={selectedOrganization}
        // relationShipManagers={relationShipManagers}
        />
      )}
    </>
  );
};

export default List;
